import React, { useState, useEffect, useRef } from 'react';
import { useCookieConsent } from '../hooks/useCookieConsent';
import '../styles/CookieBanner.css';

// Basic translation structure
interface Translations {
  banner: {
    title: string;
    description: string;
    acceptAll: string;
    rejectAll: string;
    customize: string;
  };
  modal: {
    title:string;
    essentialLabel: string;
    essentialDescription: string;
    analyticsLabel: string;
    analyticsDescription: string;
    marketingLabel: string;
    marketingDescription: string;
    savePreferences: string;
    close: string; // For closing the modal
  };
}

const translations: { [key: string]: Translations } = {
  en: {
    banner: {
      title: "We Value Your Privacy",
      description: "We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking \"Accept All\", you consent to our use of cookies.",
      acceptAll: "Accept All",
      rejectAll: "Reject All",
      customize: "Customize",
    },
    modal: {
      title: "Customize Cookie Preferences",
      essentialLabel: "Essential Cookies",
      essentialDescription: "These cookies are necessary for the website to function and cannot be switched off. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences, logging in or filling in forms.",
      analyticsLabel: "Analytics Cookies",
      analyticsDescription: "These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us to know which pages are the most and least popular and see how visitors move around the site.",
      marketingLabel: "Marketing Cookies",
      marketingDescription: "These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites.",
      savePreferences: "Save Preferences",
      close: "Close"
    },
  },
  de: {
    banner: {
      title: "Wir schätzen Ihre Privatsphäre",
      description: "Wir verwenden Cookies, um Ihr Surferlebnis zu verbessern, personalisierte Anzeigen oder Inhalte bereitzustellen und unseren Datenverkehr zu analysieren. Indem Sie auf \"Alle akzeptieren\" klicken, stimmen Sie unserer Verwendung von Cookies zu.",
      acceptAll: "Alle akzeptieren",
      rejectAll: "Alle ablehnen",
      customize: "Anpassen",
    },
    modal: {
      title: "Cookie-Einstellungen anpassen",
      essentialLabel: "Notwendige Cookies",
      essentialDescription: "Diese Cookies sind für die Funktion der Webseite erforderlich und können nicht deaktiviert werden. Sie werden in der Regel nur als Reaktion auf von Ihnen getätigte Aktionen gesetzt, die einer Dienstanforderung entsprechen, wie beispielsweise das Festlegen Ihrer Datenschutzeinstellungen, das Anmelden oder das Ausfüllen von Formularen.",
      analyticsLabel: "Analyse-Cookies",
      analyticsDescription: "Diese Cookies ermöglichen es uns, Besuche und Verkehrsquellen zu zählen, damit wir die Leistung unserer Website messen und verbessern können. Sie helfen uns zu erkennen, welche Seiten am beliebtesten und welche am wenigsten beliebt sind, und wie sich Besucher auf der Website bewegen.",
      marketingLabel: "Marketing-Cookies",
      marketingDescription: "Diese Cookies können über unsere Website von unseren Werbepartnern gesetzt werden. Sie können von diesen Unternehmen verwendet werden, um ein Profil Ihrer Interessen zu erstellen und Ihnen relevante Anzeigen auf anderen Websites anzuzeigen.",
      savePreferences: "Einstellungen speichern",
      close: "Schließen"
    },
  },
};

const CookieBanner: React.FC = () => {
  const {
    preferences,
    hasMadeChoice,
    isLoading,
    acceptAllCookies,
    rejectAllCookies,
    saveCustomPreferences,
  } = useCookieConsent();
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'de'>('en');
  
  // Reference for modal close button to manage focus
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  
  // Local state for toggles in the modal
  const [modalAnalytics, setModalAnalytics] = useState<boolean>(preferences.analytics);
  const [modalMarketing, setModalMarketing] = useState<boolean>(preferences.marketing);

  useEffect(() => {
    // Update modal toggles if preferences change (e.g. loaded from localStorage)
    setModalAnalytics(preferences.analytics);
    setModalMarketing(preferences.marketing);
  }, [preferences.analytics, preferences.marketing]);

  useEffect(() => {
    // Determine browser language
    const browserLang = navigator.language.split('-')[0];
    if (browserLang === 'de') {
      setCurrentLanguage('de');
    } else {
      setCurrentLanguage('en');
    }
  }, []);

  useEffect(() => {
    // Show banner if not loading and no choice has been made
    if (!isLoading && !hasMadeChoice) {
      setShowBanner(true);
    } else {
      setShowBanner(false);
    }
  }, [isLoading, hasMadeChoice]);

  // In CookieBanner.tsx
  useEffect(() => {
    const openModalHandler = () => {
      setModalAnalytics(preferences.analytics);
      setModalMarketing(preferences.marketing);
      setShowModal(true);
      setShowBanner(false);
    };
    window.addEventListener('openCookieSettingsModal', openModalHandler);
    return () => {
      window.removeEventListener('openCookieSettingsModal', openModalHandler);
    };
  }, [preferences.analytics, preferences.marketing]); // Add dependencies if they are used inside handler directly

  const handleAcceptAll = () => {
    acceptAllCookies();
    setShowBanner(false);
    setShowModal(false);
  };

  const handleRejectAll = () => {
    rejectAllCookies();
    setShowBanner(false);
    setShowModal(false);
  };

  const handleCustomize = () => {
    // Sync modal toggles with current preferences before opening
    setModalAnalytics(preferences.analytics);
    setModalMarketing(preferences.marketing);
    setShowModal(true);
    setShowBanner(false); // Optionally hide banner when modal is open
  };  const handleSaveModalPreferences = () => {
    saveCustomPreferences({
      analytics: modalAnalytics,
      marketing: modalMarketing,
    });
    setShowModal(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    // If no choice was made before opening modal, and modal is closed without saving,
    // decide if banner should reappear. For now, assume banner hidden by customize click.
    // Re-show banner if no choice has been made yet.
    if (!hasMadeChoice) {
        setShowBanner(true);
    }
  };
  
  // Expose a function to open the modal externally (e.g., from footer link)
  // This might be better handled via context or a global state manager later.
  // For now, we can add a temporary mechanism if needed, or address in step 6.
  // window.openCookieConsentModal = () => {
  //   setModalAnalytics(preferences.analytics);
  //   setModalMarketing(preferences.marketing);
  //   setShowModal(true);
  //   setShowBanner(false);
  // };

  // Effect to focus on the close button when modal opens
  useEffect(() => {
    if (showModal && closeButtonRef.current) {
      // Set focus to close button when modal opens
      closeButtonRef.current.focus();
    }
  }, [showModal]);

  if (isLoading) {
    return null; // Don't render anything while loading preferences
  }

  const t = translations[currentLanguage];

  return (
    <>      {showBanner && (
        <div className="cookie-banner" role="region" aria-label="We value your privacy">
          <div className="cookie-banner-content">
            <h3>{t.banner.title}</h3>
            <p>{t.banner.description}</p>
          </div>
          <div className="cookie-banner-actions">
            <button onClick={handleAcceptAll} className="cookie-btn cookie-btn-accept">
              {t.banner.acceptAll}
            </button>
            <button onClick={handleRejectAll} className="cookie-btn cookie-btn-reject">
              {t.banner.rejectAll}
            </button>
            <button onClick={handleCustomize} className="cookie-btn cookie-btn-customize">
              {t.banner.customize}
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <div className="cookie-modal-overlay" onClick={handleCloseModal}>
          <div className="cookie-modal" role="dialog" aria-modal="true" aria-labelledby="cookieModalTitle" onClick={(e) => e.stopPropagation()}>
            <div className="cookie-modal-header">
              <h3 id="cookieModalTitle">{t.modal.title}</h3>
              <button onClick={handleCloseModal} className="cookie-modal-close-btn" aria-label={t.modal.close} ref={closeButtonRef}>
                &times;
              </button>
            </div>
            <div className="cookie-modal-body">
              <div className="cookie-category">
                <h4 id="essential-cookies-label">{t.modal.essentialLabel}</h4>
                <p id="essential-cookies-description">{t.modal.essentialDescription}</p>
                <label className="cookie-toggle-switch disabled" htmlFor="essential-cookies">
                  <input 
                    id="essential-cookies"
                    type="checkbox" 
                    checked 
                    disabled 
                    aria-labelledby="essential-cookies-label"
                    aria-describedby="essential-cookies-description"
                    title={t.modal.essentialLabel}
                  />
                  <span className="cookie-slider round"></span>
                </label>
              </div>
              <div className="cookie-category">
                <h4 id="analytics-cookies-label">{t.modal.analyticsLabel}</h4>
                <p id="analytics-cookies-description">{t.modal.analyticsDescription}</p>
                <label className="cookie-toggle-switch" htmlFor="analytics-cookies">
                  <input
                    id="analytics-cookies"
                    type="checkbox"
                    checked={modalAnalytics}
                    onChange={(e) => setModalAnalytics(e.target.checked)}
                    aria-labelledby="analytics-cookies-label"
                    aria-describedby="analytics-cookies-description"
                    title={t.modal.analyticsLabel}
                  />
                  <span className="cookie-slider round"></span>
                </label>
              </div>
              <div className="cookie-category">
                <h4 id="marketing-cookies-label">{t.modal.marketingLabel}</h4>
                <p id="marketing-cookies-description">{t.modal.marketingDescription}</p>
                <label className="cookie-toggle-switch" htmlFor="marketing-cookies">
                  <input
                    id="marketing-cookies"
                    type="checkbox"
                    checked={modalMarketing}
                    onChange={(e) => setModalMarketing(e.target.checked)}
                    aria-labelledby="marketing-cookies-label"
                    aria-describedby="marketing-cookies-description"
                    title={t.modal.marketingLabel}
                  />
                  <span className="cookie-slider round"></span>
                </label>
              </div>
            </div>            <div className="cookie-modal-actions">
              <button onClick={handleRejectAll} className="cookie-btn cookie-btn-reject">
                {t.banner.rejectAll}
              </button>
              <button onClick={handleSaveModalPreferences} className="cookie-btn cookie-btn-save">
                {t.modal.savePreferences}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;

// Add a global function to open the modal (needed for step 6: Footer button)
// This is a simple way; a context/event emitter would be cleaner for larger apps.
export const openGlobalCookieSettingsModal = () => {
  const event = new CustomEvent('openCookieSettingsModal');
  window.dispatchEvent(event);
};
